import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';

const { FiClock, FiDollarSign, FiShoppingBag, FiCheckCircle, FiAlertCircle, FiChevronRight, FiUsers, FiPlus } = FiIcons;

/**
 * Componente Panoramica Tavoli Attivi
 * Mostra in evidenza tutti e soli i tavoli con comande aperte in sala,
 * con badge cromatici di stato, totale in tempo reale e selezione 1-tap.
 */
const ActiveTablesOverview = ({ 
  comande = [], 
  selectedTable = null, 
  onSelectTable, 
  onOpenNewTable,
  mode = 'cassa' // 'cassa' | 'cameriere'
}) => {
  // Aggrega le comande per numero tavolo
  const tablesSummary = useMemo(() => {
    const summaryMap = {};

    comande.forEach(comanda => {
      // Ignora comande già pagate o archiviate
      if (comanda.status === 'pagata') return;

      const tNumber = comanda.tavolo;
      if (!summaryMap[tNumber]) {
        summaryMap[tNumber] = {
          tavolo: tNumber,
          comande: [],
          totalAmount: 0,
          totalDishes: 0,
          hasPendingInKitchen: false,
          allDelivered: true,
          firstTimestamp: comanda.timestamp || comanda.created,
          lastTimestamp: comanda.timestamp || comanda.created
        };
      }

      const tableData = summaryMap[tNumber];
      tableData.comande.push(comanda);

      // Calcola piatti e totale
      const dishes = Array.isArray(comanda.piatti) ? comanda.piatti : [];
      dishes.forEach(p => {
        const qty = Number(p.quantita || p.quantity || 1);
        const price = Number(p.prezzo || 0);
        tableData.totalDishes += qty;
        tableData.totalAmount += (price * qty);
      });

      // Valuta stato di preparazione/consegna
      const isConsegnata = Boolean(comanda.consegnata);
      const isCompletata = comanda.status === 'completata';

      if (!isConsegnata && !isCompletata) {
        tableData.hasPendingInKitchen = true;
      }
      if (!isConsegnata) {
        tableData.allDelivered = false;
      }

      // Aggiorna timestamp
      const cTime = new Date(comanda.timestamp || comanda.created || Date.now());
      if (cTime < new Date(tableData.firstTimestamp)) tableData.firstTimestamp = cTime;
      if (cTime > new Date(tableData.lastTimestamp)) tableData.lastTimestamp = cTime;
    });

    // Ordina per orario (i tavoli in attesa da più tempo appaiono per primi)
    return Object.values(summaryMap).sort((a, b) => new Date(a.firstTimestamp) - new Date(b.firstTimestamp));
  }, [comande]);

  const globalTotal = useMemo(() => {
    return tablesSummary.reduce((acc, t) => acc + t.totalAmount, 0);
  }, [tablesSummary]);

  const totalDishesInHall = useMemo(() => {
    return tablesSummary.reduce((acc, t) => acc + t.totalDishes, 0);
  }, [tablesSummary]);

  const formatElapsedTime = (timestamp) => {
    try {
      const date = typeof timestamp === 'string' ? parseISO(timestamp) : new Date(timestamp);
      return formatDistanceToNow(date, { addSuffix: true, locale: it });
    } catch {
      return 'poco fa';
    }
  };

  return (
    <div className="mb-8">
      {/* KPI Bar Riassuntiva in Alto */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center">
          <div className="w-10 h-10 rounded-lg bg-[#5C715E]/10 flex items-center justify-center text-[#5C715E] mr-3">
            <SafeIcon icon={FiUsers} className="w-5 h-5" />
          </div>
          <div>
            <div className="text-xs text-gray-500 font-medium uppercase tracking-wider">Tavoli Attivi</div>
            <div className="text-2xl font-bold text-gray-900">{tablesSummary.length}</div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center">
          <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center text-orange-600 mr-3">
            <SafeIcon icon={FiAlertCircle} className="w-5 h-5" />
          </div>
          <div>
            <div className="text-xs text-gray-500 font-medium uppercase tracking-wider">In Cucina</div>
            <div className="text-2xl font-bold text-orange-600">
              {tablesSummary.filter(t => t.hasPendingInKitchen).length}
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center">
          <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center text-green-600 mr-3">
            <SafeIcon icon={FiShoppingBag} className="w-5 h-5" />
          </div>
          <div>
            <div className="text-xs text-gray-500 font-medium uppercase tracking-wider">Portate Totali</div>
            <div className="text-2xl font-bold text-gray-900">{totalDishesInHall}</div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center">
          <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-600 mr-3">
            <SafeIcon icon={FiDollarSign} className="w-5 h-5" />
          </div>
          <div>
            <div className="text-xs text-gray-500 font-medium uppercase tracking-wider">Conto in Sala</div>
            <div className="text-2xl font-bold text-emerald-700">€{globalTotal.toFixed(2)}</div>
          </div>
        </div>
      </div>

      {/* Header Sezione con Pulsante Nuovo Tavolo */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center">
            <div className="w-3 h-3 bg-emerald-500 rounded-full mr-2.5 animate-pulse"></div>
            Tavoli con Comande Aperte ({tablesSummary.length})
          </h2>
          <p className="text-sm text-gray-500">Tocca un tavolo per visualizzare o gestire la comanda istantaneamente</p>
        </div>

        {onOpenNewTable && (
          <button
            onClick={onOpenNewTable}
            className="inline-flex items-center justify-center px-4 py-2.5 bg-[#5C715E] hover:bg-[#4B5C4D] text-white font-medium rounded-xl shadow-sm transition-all text-sm"
          >
            <SafeIcon icon={FiPlus} className="w-4 h-4 mr-1.5" />
            Apri Altro Tavolo
          </button>
        )}
      </div>

      {/* Griglia delle Schede Tavolo Attive */}
      {tablesSummary.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-gray-200 rounded-2xl p-8 text-center">
          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3 text-gray-400">
            <SafeIcon icon={FiCheckCircle} className="w-6 h-6" />
          </div>
          <h3 className="text-base font-semibold text-gray-900 mb-1">Nessuna comanda attiva al momento</h3>
          <p className="text-sm text-gray-500 max-w-md mx-auto mb-4">
            Tutti i tavoli sono liberi o i conti sono stati chiusi. Seleziona un tavolo per inserire un nuovo ordine.
          </p>
          {onOpenNewTable && (
            <button
              onClick={onOpenNewTable}
              className="inline-flex items-center px-4 py-2 bg-[#5C715E] text-white rounded-lg text-sm font-medium hover:bg-[#4B5C4D] transition-colors"
            >
              <SafeIcon icon={FiPlus} className="w-4 h-4 mr-1.5" />
              Seleziona Tavolo
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          <AnimatePresence>
            {tablesSummary.map((item) => {
              const isSelected = selectedTable === item.tavolo;

              return (
                <motion.div
                  key={item.tavolo}
                  layout
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  onClick={() => onSelectTable(item.tavolo)}
                  className={`cursor-pointer rounded-2xl p-5 border-2 transition-all shadow-sm hover:shadow-md relative overflow-hidden ${
                    isSelected
                      ? 'border-[#5C715E] bg-[#5C715E]/5 ring-2 ring-[#5C715E]/20'
                      : 'border-gray-200 bg-white hover:border-[#5C715E]/50'
                  }`}
                >
                  {/* Barra Laterale Indicatore di Stato */}
                  <div
                    className={`absolute top-0 left-0 bottom-0 w-1.5 ${
                      item.hasPendingInKitchen
                        ? 'bg-orange-500'
                        : item.allDelivered
                        ? 'bg-emerald-500'
                        : 'bg-blue-500'
                    }`}
                  />

                  {/* Top: Numero Tavolo & Badge Stato */}
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center">
                        <span className="text-2xl font-black text-gray-900 tracking-tight">
                          Tavolo {item.tavolo}
                        </span>
                        {item.comande.length > 1 && (
                          <span className="ml-2 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full font-medium">
                            {item.comande.length} comande
                          </span>
                        )}
                      </div>
                      <div className="flex items-center text-xs text-gray-500 mt-0.5">
                        <SafeIcon icon={FiClock} className="w-3.5 h-3.5 mr-1" />
                        {formatElapsedTime(item.firstTimestamp)}
                      </div>
                    </div>

                    {/* Badge Stato */}
                    {item.hasPendingInKitchen ? (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-800">
                        <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mr-1.5 animate-pulse"></span>
                        In cucina
                      </span>
                    ) : item.allDelivered ? (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                        <SafeIcon icon={FiCheckCircle} className="w-3.5 h-3.5 mr-1 text-green-600" />
                        Servito
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                        Pronto al conto
                      </span>
                    )}
                  </div>

                  {/* Sintesi Piatti Rapida */}
                  <div className="text-xs text-gray-600 bg-gray-50 rounded-lg p-2.5 mb-3 line-clamp-2">
                    {item.comande.flatMap(c => (c.piatti || []).map(p => `${p.quantita || p.quantity || 1}x ${p.nome}`)).join(', ')}
                  </div>

                  {/* Bottom: Totale Provvisorio & CTA */}
                  <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                    <div>
                      <span className="text-xs text-gray-500 block">Totale Comanda</span>
                      <span className="text-lg font-bold text-emerald-700">
                        €{item.totalAmount.toFixed(2)}
                      </span>
                    </div>

                    <div className="flex items-center text-xs font-semibold text-[#5C715E] group">
                      <span>{mode === 'cassa' ? 'Gestisci Conto' : 'Vedi / Aggiungi'}</span>
                      <SafeIcon icon={FiChevronRight} className="w-4 h-4 ml-0.5 transform group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

export default ActiveTablesOverview;
